import {
	AdminRoleOnly,
	AllRolePermissions,
	ManagerAndAdminRoleOnly,
	PricingPlan,
	PricingPlanNames,
	UserAccess,
	UserData,
	UserRole
} from "@/Contracts/Users";
import difference from "lodash/difference";

export const checkUseAccessLevel =
	(userData: UserData) =>
	(id: string | null | undefined): boolean => {
		if (process.env.REACT_APP_NAME === "vendorApp") return true;
		switch (userData.access_level) {
			case UserAccess.SelfLevel:
				return id === userData.id;
			default:
				return true;
		}
	};

export const checkUseRoleDisplay =
	(userRole: UserRole | null) => (requiredRole?: UserRole) => {
		if (process.env.REACT_APP_NAME === "vendorApp") return true;
		const shouldRenderComponent = () => {
			switch (requiredRole) {
				case UserRole.PrimaryRole:
				case UserRole.AdminRole: {
					return !!userRole && !!AdminRoleOnly.includes(userRole);
				}

				case UserRole.SecondaryRole: {
					return (
						!!userRole &&
						!!ManagerAndAdminRoleOnly.includes(userRole)
					);
				}
				case UserRole.UserRole: {
					return true;
				}

				default:
					return false;
			}
		};
		return shouldRenderComponent();
	};

/**
 *
 * @param requiredPlan: Required pricing plan for the user to access this Feature.
 * If all users should be able to access this feature then pass **IGNITE**.
 * All the old accounts existing before pricing plan won't have any **PRICING PLAN** for their account
 * and will have access to all feature by default.
 *
 * @param requiredRole: Required User Role to access this feature.
 * If all users should be able to access this feature then pass **UserRole.UserRole**
 *
 * @description processes whether the user should be able to view feature based on the **USER ROLE** and
 * **CURRENT ACTIVE PRICING PLAN**
 */

export const checkUseFeatureAccess =
	(
		userRole: UserRole | null,
		rolePermissions?: string[],
		currentPlan?: PricingPlan
	) =>
	(
		requiredPlan: PricingPlanNames,
		requiredRole: UserRole,
		permissions?: AllRolePermissions[],
		skipPermissions?: boolean
	): boolean => {
		if (process.env.REACT_APP_NAME === "vendorApp") return true;
		// Enabling the below since we will be using permissions array from user data, as the base for default roles (without custom role support) as well.
		const customRolesFlag = true;

		const hasPermission = () => {
			if (
				rolePermissions &&
				difference(permissions, rolePermissions).length === 0
			)
				return true;
			return false;
		};

		const isFeatureInPlan = (): boolean => {
			switch (currentPlan?.name) {
				case PricingPlanNames.ENTERPRISE: {
					return (
						requiredPlan === PricingPlanNames.ENTERPRISE ||
						requiredPlan === PricingPlanNames.PRO ||
						requiredPlan === PricingPlanNames.GROWTH ||
						requiredPlan === PricingPlanNames.EXPAND ||
						requiredPlan === PricingPlanNames.LAUNCH ||
						requiredPlan === PricingPlanNames.IGNITE
					);
				}
				case PricingPlanNames.PRO: {
					return (
						requiredPlan === PricingPlanNames.PRO ||
						requiredPlan === PricingPlanNames.GROWTH ||
						requiredPlan === PricingPlanNames.EXPAND ||
						requiredPlan === PricingPlanNames.LAUNCH ||
						requiredPlan === PricingPlanNames.IGNITE
					);
				}
				case PricingPlanNames.GROWTH: {
					return (
						requiredPlan === PricingPlanNames.GROWTH ||
						requiredPlan === PricingPlanNames.EXPAND ||
						requiredPlan === PricingPlanNames.LAUNCH ||
						requiredPlan === PricingPlanNames.IGNITE
					);
				}

				case PricingPlanNames.EXPAND: {
					return (
						requiredPlan === PricingPlanNames.LAUNCH ||
						requiredPlan === PricingPlanNames.EXPAND ||
						requiredPlan === PricingPlanNames.IGNITE
					);
				}

				case PricingPlanNames.LAUNCH: {
					return (
						requiredPlan === PricingPlanNames.LAUNCH ||
						requiredPlan === PricingPlanNames.IGNITE
					);
				}

				case PricingPlanNames.IGNITE: {
					return requiredPlan === PricingPlanNames.IGNITE;
				}

				default: {
					return true;
				}
			}
		};

		const shouldRenderComponent = () => {
			switch (requiredRole) {
				case UserRole.PrimaryRole:
				case UserRole.AdminRole: {
					return !!userRole && !!AdminRoleOnly.includes(userRole);
				}

				case UserRole.SecondaryRole: {
					return (
						!!userRole &&
						!!ManagerAndAdminRoleOnly.includes(userRole)
					);
				}
				case UserRole.UserRole: {
					return true;
				}

				default:
					throw new Error(`${requiredRole} isn't a valid user role`);
			}
		};

		const hasAccess = shouldRenderComponent() && isFeatureInPlan();
		if (customRolesFlag && !skipPermissions)
			return hasAccess && hasPermission();

		return hasAccess;
	};
